<template>
  <div class="container bg-dark bg-gradient h-100 p-md-5 pt-0 text-light">
    <div class="row">
      <div class="col">
        <span></span>
      </div>
    </div>
    <!-- Visualization Section -->
    <div class="row">
      <div class="col pt-5 d-flex justify-content-center">
        <SunAndPlanetVisualization
          class="w-100"
          :greenhouseEffect="greenhouseEffect.value"
          :solarIntensity="solarIntensity.value"
          :outgoingIR="OLR"
          :incomingSolar="ASR"
          :maxIR="max_OLR"
          :maxSolar="max_ASR"
        />
      </div>
    </div>
    <!-- Solar System Visualization -->
    <div class="row">
      <div class="col d-flex justify-content-center">
        <SolarSystemVisualization
          class="w-100"
          :distanceFromSun="distanceFromSun.value"
        />
      </div>
    </div>
    <!-- Net radiant energy Bar Chart -->
    <div class="row mb-5 px-md-5">
      <div class="col">
        <NetEnergyBarChart :ASR="ASR" :OLR="OLR" :isBalanced="isBalanced" />
      </div>
    </div>
    <!-- Sliders and Text Boxes Section -->
    <div class="container px-md-5">
      <!-- Albedo -->
      <div class="row mb-2">
        <div class="col-3 text-start">
          <span id="albedo-label">Albedo</span>
        </div>
        <div class="col text-end d-flex">
          <input
            type="number"
            v-model.number="albedo.value"
            min="0"
            max="1"
            step="0.01"
            class="text-box"
            :disabled="albedo.locked"
            aria-labelledby="albedo-label"
          />
          <input
            type="range"
            v-model.number="albedo.value"
            min="0"
            max="1"
            step="0.01"
            class="flex-grow-1 mx-2"
            :disabled="albedo.locked"
            aria-labelledby="albedo-label"
          />
        </div>
      </div>

      <!-- Distance from Sun -->
      <div class="row mb-2">
        <div class="col-3 text-start">
          <span id="distance-label">Distance from Sun (A.U.)</span>
        </div>
        <div class="col text-end d-flex">
          <input
            type="number"
            v-model.number="distanceFromSun.value"
            min="0.4"
            max="5.2"
            step="0.001"
            class="text-box"
            :disabled="distanceFromSun.locked"
            aria-labelledby="distance-label"
          />
          <input
            type="range"
            v-model.number="distanceFromSun.value"
            min="0.4"
            max="5.2"
            step="0.001"
            class="flex-grow-1 mx-2"
            :disabled="distanceFromSun.locked"
            aria-labelledby="distance-label"
          />
        </div>
      </div>

      <!-- Solar Intensity -->
      <div class="row mb-2">
        <div class="col-3 text-start">
          <span id="solar-label">Solar Intensity (%)</span>
        </div>
        <div class="col text-end d-flex">
          <input
            type="number"
            v-model.number="solarIntensity.value"
            min="50"
            max="200"
            step="1"
            class="text-box"
            :disabled="solarIntensity.locked"
            aria-labelledby="solar-label"
          />
          <input
            type="range"
            v-model.number="solarIntensity.value"
            min="50"
            max="200"
            step="1"
            class="flex-grow-1 mx-2"
            :disabled="solarIntensity.locked"
            aria-labelledby="solar-label"
          />
        </div>
      </div>

      <!-- Greenhouse Effect -->
      <div class="row mb-2">
        <div class="col-3 text-start">
          <span id="greenhouse-label">Greenhouse Strength</span>
        </div>
        <div class="col text-end d-flex">
          <input
            type="number"
            v-model.number="greenhouseEffect.value"
            min="0"
            max="1"
            step="0.001"
            class="text-box"
            :disabled="greenhouseEffect.locked"
            aria-labelledby="greenhouse-label"
          />
          <input
            type="range"
            v-model.number="greenhouseEffect.value"
            min="0"
            max="1"
            step="0.001"
            class="flex-grow-1 mx-2"
            :disabled="greenhouseEffect.locked"
            aria-labelledby="greenhouse-label"
          />
        </div>
      </div>

      <!-- Surface Temperature -->
      <div class="row">
        <div class="col-3 text-start">
          <span id="temperature-label">
            Surface Temperature ({{ formattedTemperatureUnit }}):
          </span>
        </div>
        <div class="col text-end d-flex">
          <input
            type="number"
            v-model.number="sliderTemperature"
            :min="temperatureMin"
            :max="temperatureMax"
            step="0.1"
            class="text-box"
            :disabled="temperatureKelvin.locked"
            aria-labelledby="temperature-label"
          />
          <input
            type="range"
            v-show="temperatureUnit === 'K'"
            v-model.number="sliderTemperature"
            :min="55"
            :max="800"
            step="0.01"
            class="flex-grow-1 mx-2"
            :disabled="temperatureKelvin.locked"
            aria-labelledby="temperature-label"
          />
          <input
            v-show="temperatureUnit === 'C'"
            type="range"
            v-model.number="sliderTemperature"
            :min="-218.15"
            :max="526.85"
            step="0.01"
            class="flex-grow-1 mx-2"
            :disabled="temperatureKelvin.locked"
            aria-labelledby="temperature-label"
          />
          <input
            v-show="temperatureUnit === 'F'"
            type="range"
            v-model.number="sliderTemperature"
            :min="-360"
            :max="980"
            step="0.01"
            class="flex-grow-1 mx-2"
            :disabled="temperatureKelvin.locked"
            aria-labelledby="temperature-label"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NetEnergyBarChart from "@/components/activity/NetEnergyBarChart.vue";
import SolarSystemVisualization from "@/components/activity/SolarSystemVisualization.vue";
import SunAndPlanetVisualization from "@/components/activity/SunAndPlanetVisualization.vue";
import { mapGetters } from "vuex";
import labs from "@/data/labs";

export default {
  name: "ActivityView",
  components: {
    SunAndPlanetVisualization,
    SolarSystemVisualization,
    NetEnergyBarChart
  },
  data() {
    return {
      albedo: {
        value: 0.3,
        locked: false
      },
      distanceFromSun: {
        value: 1,
        locked: false
      },
      solarIntensity: {
        value: 100,
        locked: false
      },
      greenhouseEffect: {
        value: 0,
        locked: false
      },
      temperatureKelvin: {
        value: 255,
        locked: false
      },
      // Initial temperature bounds for Kelvin
      temperatureMin: 0,
      temperatureMax: 800
    };
  },
  props: {
    labID: String
  },
  computed: {
    ...mapGetters(["isAdmin", "temperatureUnit", "preset"]),
    formattedTemperatureUnit() {
      return this.temperatureUnit === "C" || this.temperatureUnit === "F"
        ? `°${this.temperatureUnit}`
        : this.temperatureUnit;
    },
    ASR() {
      // Absorbed Solar Radiation
      return (
        340 *
        (this.solarIntensity.value / 100) *
        Math.pow(1 / this.distanceFromSun.value, 2) *
        (1 - this.albedo.value)
      );
    },
    OLR() {
      // Outgoing Longwave Radiation
      return (
        5.67e-8 *
        Math.pow(this.temperatureKelvin.value, 4) *
        (1 - this.greenhouseEffect.value)
      );
    },
    max_ASR() {
      // Maximum Absorbed Solar Radiation
      return 340 * 2 * Math.pow(1 / 0.4, 2);
    },
    max_OLR() {
      // Maximum Outgoing Longwave Radiation
      return 5.67e-8 * Math.pow(800, 4);
    },
    netEnergy() {
      // Net radiant energy = ASR - OLR
      return this.ASR - this.OLR;
    },
    isBalanced() {
      // Energy is balanced if the absolute net energy is within 10 Wm^-2
      return Math.abs(this.netEnergy) <= 10;
    },
    sliderTemperature: {
      get() {
        // Rounds the temperature to 2 decimal places
        let temp;
        if (this.temperatureUnit === "C")
          temp = this.temperatureKelvin.value - 273.15;
        if (this.temperatureUnit === "F")
          temp = (this.temperatureKelvin.value - 273.15) * (9 / 5) + 32;
        if (this.temperatureUnit === "K") temp = this.temperatureKelvin.value;
        return this.formatInputVal(
          temp,
          this.temperatureMin,
          this.temperatureMax,
          2
        );
      },
      set(value) {
        let formattedValue = this.formatInputVal(
          value,
          this.temperatureMin,
          this.temperatureMax,
          2
        );
        if (this.temperatureUnit === "C") formattedValue = value + 273.15;
        if (this.temperatureUnit === "F")
          formattedValue = ((value - 32) * 5) / 9 + 273.15;

        this.temperatureKelvin.value = formattedValue;
      }
    }
  },
  methods: {
    formatInputVal(newVal, min, max, decimalPlace) {
      if (newVal < min) return min;
      if (newVal > max) return max;
      return Number(newVal.toFixed(decimalPlace));
    },
    resetLockedVariables() {
      this.albedo.locked = false;
      this.distanceFromSun.locked = false;
      this.solarIntensity.locked = false;
      this.greenhouseEffect.locked = false;
      this.temperatureKelvin.locked = false;
    },
    setLockedVariables() {
      const lab = labs.find((lab) => lab.id === this.labID);
      if (!lab && this.labID?.length > 0) {
        console.error("Lab not found: " + this.labID);
        this.$router.push("/404");
      }
      this.resetLockedVariables();
      lab?.lockedVariables.forEach((variable) => {
        this[variable].locked = true;
      });
    },
    setTemperatureMin() {
      if (this.temperatureUnit === "K") return 0;
      if (this.temperatureUnit === "C") return -218.15;
      if (this.temperatureUnit === "F") return -360;
      throw new Error(
        "Invalid temperature unit: Expected C | F | K: got " +
          this.temperatureUnit
      );
    },
    setTemperatureMax() {
      if (this.temperatureUnit === "C") return 526.85;
      if (this.temperatureUnit === "F") return 980;
      if (this.temperatureUnit === "K") return 800;
      throw new Error(
        "Invalid temperature unit: Expected C | F | K: got " +
          this.temperatureUnit
      );
    }
  },
  mounted() {
    this.setLockedVariables();
  },
  watch: {
    labID() {
      this.setLockedVariables();
    },
    temperatureUnit() {
      // Update the displayed min and max when the temperature unit changes
      this.temperatureMin = this.setTemperatureMin();
      this.temperatureMax = this.setTemperatureMax();
    },
    preset(newVal, oldVal) {
      if (!newVal) return;
      if (newVal !== oldVal) {
        this.albedo.value = newVal.albedo;
        this.distanceFromSun.value = newVal.distanceFromSun;
        this.solarIntensity.value = newVal.solarIntensity;
        this.greenhouseEffect.value = newVal.greenhouseEffect;
        this.temperatureKelvin.value = newVal.temperatureKelvin;
      }
    },
    // Watchers to enforce min/max and rounding on number inputs
    "albedo.value"(newVal) {
      this.albedo.value = this.formatInputVal(newVal, 0, 1, 2);
    },
    "distanceFromSun.value"(newVal) {
      this.distanceFromSun.value = this.formatInputVal(newVal, 0.4, 5.2, 3);
    },
    "solarIntensity.value"(newVal) {
      this.solarIntensity.value = this.formatInputVal(newVal, 50, 200, 0);
    },
    "greenhouseEffect.value"(newVal) {
      this.greenhouseEffect.value = this.formatInputVal(newVal, 0, 1, 3);
    }
  }
};
</script>

<style scoped>
.text-box {
  width: 80px;
}
</style>
